import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi, companiesApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import Toast from '../../components/Toast'
import type { User, Company } from '../../types'
import { SCOPE_DEFS, SCOPE_EDGE_USERS } from '../../config/apps'

const roleLabel = (r: string) => ({ Admin: '관리자', Manager: '매니저', User: '사용자' }[r] ?? r)
const roleColor = (r: string) => ({
  Admin: 'bg-purple-100 text-purple-700',
  Manager: 'bg-teal-100 text-teal-800',
  User: 'bg-gray-100 text-gray-600',
}[r] ?? 'bg-gray-100 text-gray-600')

/** 보유 scope 배지 — 첫 번째만 노출하고 나머지는 "외 N개" 클릭 시 바로 아래 팝오버로 전체 목록 표시 */
function ScopeBadges({ scopes }: { scopes?: string[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  const defs = SCOPE_DEFS.filter(d => scopes?.includes(d.value))

  // 팝오버 밖 클릭으로 닫기
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  if (defs.length === 0) return null

  // 역할 칩 옆에 "[N개 권한]" 태그 하나 — scope 이름을 앞에 내밀면 역할 라벨(사용자 등)과 헷갈린다.
  // 클릭하면 전체 목록 팝오버.
  return (
    <span ref={ref} className="relative inline-block">
      <button onClick={() => setOpen(o => !o)}
        className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition">
        {defs.length}개 권한
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 bg-surface border border-gray-200 rounded-lg shadow-lg px-3 py-2 whitespace-nowrap">
          {defs.map(d => (
            <div key={d.value} className="text-xs text-gray-700 py-0.5" title={d.hint}>
              {d.label}
            </div>
          ))}
        </div>
      )}
    </span>
  )
}

function SessionBadge({ user }: { user: User }) {
  if (user.hasActiveSession)
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">온라인</span>
  return (
    <div>
      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">오프라인</span>
      {user.tokensInvalidatedAt && (
        <div className="text-gray-400 text-xs mt-0.5">
          {new Date(user.tokensInvalidatedAt).toLocaleString('ko-KR')}
        </div>
      )}
    </div>
  )
}

export default function PortalUsers() {
  const qc = useQueryClient()
  const { userId, role, can } = useAuthStore()
  // 전 회사 대상(회사 열·초대 회사 선택·토큰 만료): Admin 또는 edge:users(Edge팀)
  const canAllUsers = can(SCOPE_EDGE_USERS)
  // 권한 부여(scope, Admin 역할)는 실제 Admin 계정만 — scope 보유 Edge팀은 불가 (서버도 403)
  const isTenantAdmin = role === 'Admin'

  const [search, setSearch] = useState('')
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteCompanyId, setInviteCompanyId] = useState('')
  const [editUser, setEditUser] = useState<User | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const closeToast = useCallback(() => setToast(null), [])

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll().then(r => r.data as User[]),
    staleTime: 0,
    refetchInterval: 30_000,
  })

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => companiesApi.getAll().then(r => r.data as Company[]),
    enabled: canAllUsers,
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = q
      ? users.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      : [...users]
    return list.sort((a, b) => {
      if (a.hasActiveSession !== b.hasActiveSession) return a.hasActiveSession ? -1 : 1
      if (a.isEmailVerified !== b.isEmailVerified) return a.isEmailVerified ? -1 : 1
      return 0
    })
  }, [users, search])

  const inviteMutation = useMutation({
    mutationFn: () => usersApi.invite(inviteEmail, canAllUsers ? inviteCompanyId || undefined : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setShowInvite(false)
      setInviteEmail('')
      setInviteCompanyId('')
      setToast({ message: '초대 메일 발송 완료', type: 'success' })
    },
    onError: (err) => setToast({ message: getErrorMessage(err, '초대 발송에 실패했습니다.'), type: 'error' }),
  })

  const updateMutation = useMutation({
    // scopes 는 Admin 만 보낼 수 있다 — Manager 는 undefined 로 두어 기존 값 유지
    mutationFn: (u: User) => usersApi.update(
      u.id, u.name, u.role,
      isTenantAdmin ? u.scopes : undefined,
      isTenantAdmin ? u.companyId || undefined : undefined,
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditUser(null)
      setToast({ message: '사용자 정보가 수정되었습니다.', type: 'success' })
    },
    onError: (err) => setToast({ message: getErrorMessage(err, '수정에 실패했습니다.'), type: 'error' }),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setToast({ message: '사용자가 삭제되었습니다.', type: 'success' })
    },
    onError: (err) => setToast({ message: getErrorMessage(err, '삭제에 실패했습니다.'), type: 'error' }),
  })

  const revokeTokensMutation = useMutation({
    mutationFn: (id: string) => usersApi.revokeTokens(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
      setToast({ message: '토큰이 강제 만료되었습니다.', type: 'success' })
    },
    onError: (err) => setToast({ message: getErrorMessage(err, '토큰 강제 만료에 실패했습니다.'), type: 'error' }),
  })

  const canInvite = !!inviteEmail && (canAllUsers ? !!inviteCompanyId : true) && !inviteMutation.isPending
  const colCount = canAllUsers ? 7 : 6

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-gray-900">사용자</h2>
        </div>
        <button onClick={() => setShowInvite(true)}
          className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition">
          + 사용자 초대
        </button>
      </div>

      <div className="mb-4">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="이름 또는 이메일로 검색"
          className="w-full max-w-sm bg-surface border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/25 focus:border-teal-600"
        />
      </div>

      {/* overflow-hidden 을 쓰면 권한 팝오버(absolute)가 마지막 행에서 잘린다 — 모서리는 thead 셀에 직접 준다 */}
      <div className="bg-surface rounded-xl border border-gray-200/80 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs [&>tr>th:first-child]:rounded-tl-xl [&>tr>th:last-child]:rounded-tr-xl">
            <tr>
              <th className="text-left px-4 py-3">이름</th>
              <th className="text-left px-4 py-3">이메일</th>
              {canAllUsers && <th className="text-left px-4 py-3">회사</th>}
              <th className="text-left px-4 py-3">권한</th>
              <th className="text-left px-4 py-3">로그인 상태</th>
              <th className="text-left px-4 py-3">가입일</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-4 py-8 text-center text-gray-400 text-xs">
                  {search ? '검색 결과가 없습니다.' : '사용자가 없습니다.'}
                </td>
              </tr>
            )}
            {filtered.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
                  {u.name || <span className="text-gray-300">미설정</span>}
                  {/* 인증 완료가 대부분이라 컬럼 대신 미인증만 표시 */}
                  {!u.isEmailVerified && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">미인증</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-500">{u.email}</td>
                {canAllUsers && (
                  <td className="px-4 py-3 text-gray-500 text-xs">{u.company?.name ?? <span className="text-gray-300">-</span>}</td>
                )}
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColor(u.role)}`}>
                    {roleLabel(u.role)}
                  </span>
                  <ScopeBadges scopes={u.scopes} />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <SessionBadge user={u} />
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{new Date(u.createdAt).toLocaleDateString('ko-KR')}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3 justify-end">
                    {u.email !== 'admin' && (
                      <button onClick={() => setEditUser({ ...u })} className="text-teal-700 hover:text-teal-900 text-xs">수정</button>
                    )}
                    {canAllUsers && u.id !== userId && u.email !== 'admin' && u.hasActiveSession && (
                      <button
                        onClick={() => {
                          if (confirm(`${u.name || u.email}의 토큰을 강제 만료하시겠습니까?`))
                            revokeTokensMutation.mutate(u.id)
                        }}
                        className="text-orange-500 hover:text-orange-700 text-xs">만료</button>
                    )}
                    {u.id !== userId && u.email !== 'admin' && (
                      <button
                        onClick={() => {
                          if (confirm(`${u.name || u.email}을(를) 삭제하시겠습니까?`))
                            deleteMutation.mutate(u.id)
                        }}
                        className="text-red-400 hover:text-red-600 text-xs">삭제</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 초대 모달 */}
      {showInvite && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-4">사용자 초대</h3>
            <div className="space-y-3 mb-4">
              {canAllUsers && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">회사 선택</label>
                  <select
                    value={inviteCompanyId}
                    onChange={e => setInviteCompanyId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                    <option value="">회사를 선택하세요</option>
                    {companies.filter(c => c.isActive).map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">이메일</label>
                <input
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  type="email"
                  placeholder="초대할 이메일 주소"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => inviteMutation.mutate()}
                disabled={!canInvite}
                className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition disabled:opacity-50 flex items-center gap-2">
                {inviteMutation.isPending ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    요청 중...
                  </>
                ) : '초대 발송'}
              </button>
              <button
                onClick={() => { setShowInvite(false); setInviteEmail(''); setInviteCompanyId('') }}
                disabled={inviteMutation.isPending}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {editUser && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-4">사용자 수정</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">이름</label>
                <input
                  value={editUser.name}
                  onChange={e => setEditUser({ ...editUser, name: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">권한</label>
                <select
                  value={editUser.role}
                  onChange={e => setEditUser({ ...editUser, role: e.target.value as User['role'] })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  <option value="User">사용자</option>
                  <option value="Manager">매니저</option>
                  {(isTenantAdmin || editUser.role === 'Admin') && <option value="Admin">관리자</option>}
                </select>
              </div>
              {canAllUsers && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">회사</label>
                  {isTenantAdmin ? (
                    // 실제 Admin 만 소속 회사를 바꿀 수 있다 — 예: 기존 Admin 계정을 DMTEK 소속 Manager/User + Edge팀으로 전환
                    <select
                      value={editUser.companyId ?? ''}
                      onChange={e => setEditUser({ ...editUser, companyId: e.target.value || undefined })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                      {!editUser.companyId && <option value="">- 소속 없음 -</option>}
                      {companies.filter(c => c.isActive || c.id === editUser.companyId).map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-600 px-1">{editUser.company?.name ?? '-'}</p>
                  )}
                </div>
              )}
              {isTenantAdmin && editUser.role !== 'Admin' && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">추가 권한</label>
                  <div className="space-y-1.5">
                    {SCOPE_DEFS.map(d => (
                      <label key={d.value} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                        <input type="checkbox" className="rounded border-gray-300 mt-0.5"
                          checked={(editUser.scopes ?? []).includes(d.value)}
                          onChange={e => setEditUser({
                            ...editUser,
                            scopes: e.target.checked
                              ? [...(editUser.scopes ?? []), d.value]
                              : (editUser.scopes ?? []).filter(s => s !== d.value),
                          })} />
                        <span>
                          {d.label}
                          <span className="block text-xs text-gray-400">{d.hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-1.5">소속 회사 유지, 보드 등 다른 서비스에서는 일반 사용자</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => updateMutation.mutate(editUser)}
                disabled={updateMutation.isPending}
                className="bg-ink text-on-ink px-4 py-2 rounded-lg text-sm hover:bg-ink-hover transition disabled:opacity-50">
                저장
              </button>
              <button
                onClick={() => setEditUser(null)}
                className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm">
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
